import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  FavoriteSitter,
  FavoriteSitterService,
  PagedResponseMeta,
  Parent,
  ParentService,
  Sitter,
  SitterService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';
import { BookedSittersCard } from './components/BookedSittersCard';

export const BookedSitters = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const parentId = pathId ? Number(pathId) : undefined;
  const pageParam = Number(searchParams.get('page') || 1) || 1;

  const [sitters, setSitters] = useState<Sitter[]>([]);
  const [meta, setMeta] = useState<PagedResponseMeta | null>(null);
  const [viewedParent, setViewedParent] = useState<Parent | null>(null);
  const [loading, setLoading] = useState(true);

  const canEdit = !!user?.id && !!parentId && user.id === parentId;

  useEffect(() => {
    document.title = 'UrbanSitter - Booked Caregivers';
  }, []);

  const fetchSitters = useCallback(
    async (pid: number, uid: number, page: number): Promise<void> => {
      setLoading(true);
      try {
        const resp = (
          await SitterService.getByFilter({
            params: {
              parentId: pid,
              userId: uid,
              fields: 'friendRelationship',
              booked: true,
              page,
            },
          })
        ).data;
        setSitters(resp.data);
        setMeta(resp.meta);
      } catch (err) {
        console.warn('[BookedSitters] load failed', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!user?.id || !parentId) return;
    fetchSitters(parentId, user.id, pageParam);
  }, [user?.id, parentId, pageParam, fetchSitters]);

  useEffect(() => {
    if (!user?.id || !parentId || canEdit) {
      setViewedParent(null);
      return;
    }
    const load = async (): Promise<void> => {
      try {
        const resp = await ParentService.get(parentId, { params: { userId: user.id } });
        setViewedParent(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[BookedSitters] parent load failed', err);
      }
    };
    void load();
  }, [user?.id, parentId, canEdit]);

  const setFavorite = async (
    isFavorited: boolean,
    favoriteId: number,
    sitterId: number,
  ): Promise<void> => {
    if (!user?.id) return;
    const body: FavoriteSitter = { parentId: user.id, sitterId };
    if (isFavorited) {
      const resp = await FavoriteSitterService.create(body);
      const createdId = resp.data?.data?.id;
      setSitters((prev) =>
        prev.map((s) =>
          s.id === sitterId
            ? {
                ...s,
                relationship: {
                  ...(s.relationship ?? {}),
                  bookmarked: true,
                  favorite: { ...(s.relationship?.favorite ?? {}), id: createdId },
                },
              }
            : s,
        ),
      );
    } else {
      await FavoriteSitterService.delete(favoriteId);
      setSitters((prev) =>
        prev.map((s) =>
          s.id === sitterId
            ? {
                ...s,
                relationship: {
                  ...(s.relationship ?? {}),
                  bookmarked: false,
                  favorite: undefined,
                },
              }
            : s,
        ),
      );
    }
  };

  const openSitterProfile = (sitter: Sitter): void => {
    if (sitter.id == null) return;
    navigate(generatePath(Routes.sitter.profile, { id: String(sitter.id) }));
  };

  const goToBooking = (sitter: Sitter): void => {
    if (!user?.id || !sitter.id) return;
    void tracker.track('my-friends-sitters', {
      action: 'Booking CTA clicked',
      label: 'booked sitter list',
      value: parentId,
    });
    const params = new URLSearchParams({
      userId: String(user.id),
      parentId: String(user.id),
      sitterId: String(sitter.id),
    });
    navigate(`${Routes.parent.bookSitter}?${params.toString()}`);
  };

  const goToPage = (page: number): void => {
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set('page', String(page));
      return next;
    });
  };

  const cardParent: Parent | null = canEdit
    ? (user as Parent | null) ?? null
    : viewedParent;

  const titlePrefix = canEdit
    ? t('pages.bookedSitters.titleYour')
    : viewedParent?.firstName
      ? t('pages.bookedSitters.titleOthers', { name: viewedParent.firstName })
      : '';

  const isMobile = useMemo(
    () =>
      typeof navigator !== 'undefined' && /iPhone|iPad|Android/.test(navigator.userAgent),
    [],
  );

  const [shareCopied, setShareCopied] = useState(false);
  const shareCopiedTimeoutRef = useRef<number | null>(null);

  useEffect(() => (): void => {
    if (shareCopiedTimeoutRef.current != null) {
      window.clearTimeout(shareCopiedTimeoutRef.current);
    }
  }, []);

  const handleShare = async (): Promise<void> => {
    const shareUrl = window.location.href;
    const shareText = t('pages.bookedSitters.shareText');
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: shareText, text: shareText, url: shareUrl });
        return;
      } catch {
        // fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success(t('common.linkCopied'));
      setShareCopied(true);
      if (shareCopiedTimeoutRef.current != null) {
        window.clearTimeout(shareCopiedTimeoutRef.current);
      }
      shareCopiedTimeoutRef.current = window.setTimeout(() => {
        setShareCopied(false);
        shareCopiedTimeoutRef.current = null;
      }, 2000);
    } catch {
      toast.error(t('pages.bookedSitters.unableToShare'));
    }
  };

  const half1 = sitters.slice(0, Math.ceil(sitters.length / 2));
  const half2 = sitters.slice(Math.ceil(sitters.length / 2));

  if (loading) {
    return (
      <div className='container py-8'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      {isMobile && (
        <div id='branch-banner' className='bg-sapphire-50 border border-sapphire rounded p-3 mb-4 text-center'>
          <a
            href='https://urbansitter.app.link/'
            className='font-semibold underline'
          >
            {t('pages.bookedSitters.installAppBanner')}
          </a>
        </div>
      )}
      <div className='flex items-center justify-between mb-6 flex-wrap gap-2'>
        <h1 className='text-2xl font-semibold'>
          {titlePrefix} {t('pages.bookedSitters.heading')}
        </h1>
        <div className='relative'>
          <Button
            buttonType={ButtonType.secondary}
            id='share-booked-sitters'
            onClick={handleShare}
          >
            <i className='fa-solid fa-share mr-1' /> {t('pages.bookedSitters.share')}
          </Button>
          {shareCopied && (
            <span
              role='status'
              className='absolute right-0 top-full mt-1 bg-neutral-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg'
            >
              {t('common.linkCopied')}
            </span>
          )}
        </div>
      </div>

      {sitters.length === 0 ? (
        <p className='text-neutral-600'>{t('pages.bookedSitters.empty')}</p>
      ) : (
        <>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='flex flex-col'>
              {cardParent && half1.map((sitter) => (
                <BookedSittersCard
                  key={sitter.id}
                  sitter={sitter}
                  parent={cardParent}
                  currentUserId={user?.id}
                  setFavorite={setFavorite}
                  onBook={goToBooking}
                  onOpenProfile={openSitterProfile}
                />
              ))}
            </div>
            <div className='flex flex-col'>
              {cardParent && half2.map((sitter) => (
                <BookedSittersCard
                  key={sitter.id}
                  sitter={sitter}
                  parent={cardParent}
                  currentUserId={user?.id}
                  setFavorite={setFavorite}
                  onBook={goToBooking}
                  onOpenProfile={openSitterProfile}
                />
              ))}
            </div>
          </div>

          {meta && meta.pageCount !== undefined && meta.pageCount > 1 && (
            <div className='flex items-center justify-center gap-3 mt-6'>
              <Button
                buttonType={ButtonType.secondary}
                disabled={(meta.page ?? 1) <= 1}
                onClick={(): void => goToPage((meta.page ?? 1) - 1)}
              >
                {t('common.previous')}
              </Button>
              <span className='text-sm text-neutral-700'>
                {t('pages.bookedSitters.pageOf', { current: meta.page, total: meta.pageCount })}
              </span>
              <Button
                buttonType={ButtonType.secondary}
                disabled={meta.lastPage === true || (meta.page ?? 1) >= (meta.pageCount ?? 1)}
                onClick={(): void => goToPage((meta.page ?? 1) + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
});
